import { CompanyPeopleList_organization$key } from '#app/__generated__/relay/CompanyPeopleList_organization.graphql.js';
import { AvatarWithDetails } from '#app/components/Avatars/AvatarWithDetails/AvatarWithDetails.js';
import { Button } from '#app/components/Buttons/Button.js';
import { INTERNAL_ONLY_WARNING_COPY } from '#app/components/companyProfilesV2/types.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { Text } from '#app/components/Primitives/Text.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { CreateCard } from '#app/features/profile/components/CreateCard/CreateCard.js';
import { formatName } from '#app/utilities/formatName.js';
import { OverviewTeamSectionEmptyState } from '../EmptyStates/OverviewTeamSectionEmptyState.js';
import { EyeOffIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  padding: 24px;

  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colorsV2.gray30};
`;

const CompanyTitle = styled(Text)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    font-size: 18px;
    line-height: 28px;
    letter-spacing: -1%;
    color: ${theme.colorsV2.black};
  `};
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const FullWidth = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  gap: 32px;

  & > * {
    width: 100%;
  }
`;

export const CompanyPeopleList = ({
  inviteTeamMembersCTA,
  onNavigateToPeopleTab,
  organizationRef,
  showInternalOnlyWarning,
}: {
  readonly inviteTeamMembersCTA: boolean;
  readonly onNavigateToPeopleTab?: () => void;
  readonly organizationRef: CompanyPeopleList_organization$key;
  readonly showInternalOnlyWarning?: boolean;
}) => {
  const organization = useFragment<CompanyPeopleList_organization$key>(
    graphql`
      fragment CompanyPeopleList_organization on Organization {
        visitorIsMember
        name
        ...OverviewTeamSectionEmptyState_organization
        publicMembers(first: $publicMembersFirst) {
          count
          edges {
            node {
              id
              userProfile {
                id
                firstName
                lastName
                displayUsername
                professionalTitle
                avatarImage {
                  id
                  uid
                  url
                }
              }
            }
          }
        }
      }
    `,
    organizationRef,
  );

  const { openModal } = useModal({
    identifier: 'InviteTeammatesModal',
    modalProps: {},
  });

  if (!organization.publicMembers || !organization.publicMembers.edges.length) {
    return (
      <Container>
        <OverviewTeamSectionEmptyState organizationRef={organization} />
      </Container>
    );
  }

  const publicMembers = organization.publicMembers.edges.map(
    ({ node }) => node.userProfile,
  );

  return (
    <Container>
      <TitleContainer>
        <CompanyTitle>
          {organization.name} Team
          {publicMembers.length > 0 ? (
            <> ({organization.publicMembers.count})</>
          ) : null}
          {showInternalOnlyWarning && (
            <Tooltip
              content={INTERNAL_ONLY_WARNING_COPY}
              placement="top"
            >
              <EyeOffIcon size={16} />
            </Tooltip>
          )}
        </CompanyTitle>
        {inviteTeamMembersCTA ? null : onNavigateToPeopleTab ? (
          <Button
            onClick={onNavigateToPeopleTab}
            variant="V2Link"
          >
            View team
          </Button>
        ) : null}
      </TitleContainer>

      {inviteTeamMembersCTA && organization.visitorIsMember ? (
        <CreateCard
          onCreateClick={() => {
            openModal();
          }}
          variant="invite-teammates"
        />
      ) : null}
      <FullWidth>
        {publicMembers.map((userProfile) => {
          if (!userProfile.id) {
            return null;
          }

          const {
            avatarImage,
            displayUsername,
            firstName,
            id,
            lastName,
            professionalTitle,
          } = userProfile;

          return (
            <AvatarWithDetails
              avatarImage={
                avatarImage?.uid
                  ? {
                      id: avatarImage.id,
                      uid: avatarImage.uid,
                      url: avatarImage.url,
                    }
                  : undefined
              }
              displayUsername={displayUsername}
              key={id}
              size="sm"
              subtitle={professionalTitle}
              userName={formatName({ firstName, lastName }, 'fullName')}
            />
          );
        })}
      </FullWidth>
    </Container>
  );
};
